import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, Check, ShieldCheck, Loader2, CalendarDays, PenLine,
  CheckCircle2, Download, Building2, User, Mail, Phone, Sparkles, Globe,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/contract/SignaturePad";
import LanguageGate from "@/components/contract/LanguageGate";
import { translations } from "@/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_77e99736-a114-44cf-bdab-fa0372b7c2a2/artifacts/nx8mcx1b_Zaynar%20Systems%20logo.png.png";
const HERO_TEXTURE = "https://images.unsplash.com/photo-1634549709262-508c47d4c229?crop=entropy&cs=srgb&fm=jpg&q=85";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const Overline = ({ children }) => (
  <span className="text-xs font-sans uppercase tracking-[0.25em] text-gold">{children}</span>
);

export default function ContractPage() {
  const [entered, setEntered] = useState(false);
  const [lang, setLang] = useState("en");
  const [config, setConfig] = useState(null);
  const t = config?.ui || translations.en;
  const inviteToken = new URLSearchParams(window.location.search).get("invite") || "";
  const [inviteValid, setInviteValid] = useState(null);

  useEffect(() => {
    if (!inviteToken) { setInviteValid(false); return; }
    axios.get(`${API}/invite/${inviteToken}`)
      .then((r) => setInviteValid(!!r.data.valid))
      .catch(() => setInviteValid(false));
  }, [inviteToken]);
  const [addons, setAddons] = useState([]);
  const [monthly, setMonthly] = useState(false);
  const [form, setForm] = useState({ full_name: "", company_name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [checking, setChecking] = useState({ email: false, phone: false });
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const sigRef = useRef(null);

  useEffect(() => {
    setConfig(null);
    axios.get(`${API}/contract/config?lang=${lang}`)
      .then((r) => setConfig(r.data))
      .catch(() => toast.error(t.toastLoad));
    // eslint-disable-next-line
  }, [lang]);

  const toggleAddon = (id) =>
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const onField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const onPhoneInput = (value) => {
    const cleaned = value.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
    onField("phone", cleaned);
  };

  const validateEmail = useCallback(async () => {
    if (!form.email.trim()) { setErrors((e) => ({ ...e, email: t.errEmailRequired })); return false; }
    setChecking((c) => ({ ...c, email: true }));
    try {
      const { data } = await axios.post(`${API}/validate/email`, { email: form.email });
      if (!data.valid) { setErrors((e) => ({ ...e, email: t.errEmailInvalid })); return false; }
      setErrors((e) => ({ ...e, email: undefined })); return true;
    } catch { setErrors((e) => ({ ...e, email: t.errEmailVerify })); return false; }
    finally { setChecking((c) => ({ ...c, email: false })); }
  }, [form.email, t]);

  const validatePhone = useCallback(async () => {
    if (!form.phone.trim()) { setErrors((e) => ({ ...e, phone: t.errPhoneRequired })); return false; }
    setChecking((c) => ({ ...c, phone: true }));
    try {
      const { data } = await axios.post(`${API}/validate/phone`, { phone: form.phone });
      if (!data.valid) { setErrors((e) => ({ ...e, phone: t.errPhoneInvalid })); return false; }
      setErrors((e) => ({ ...e, phone: undefined })); return true;
    } catch { setErrors((e) => ({ ...e, phone: t.errPhoneVerify })); return false; }
    finally { setChecking((c) => ({ ...c, phone: false })); }
  }, [form.phone, t]);

  const handleSubmit = async () => {
    const newErr = {};
    if (!form.full_name.trim()) newErr.full_name = t.errFullName;
    if (!form.company_name.trim()) newErr.company_name = t.errCompany;
    setErrors((e) => ({ ...e, ...newErr }));

    const emailOk = await validateEmail();
    const phoneOk = await validatePhone();

    if (Object.keys(newErr).length || !emailOk || !phoneOk) { toast.error(t.toastFields); return; }
    if (sigRef.current?.isEmpty()) { toast.error(t.toastSignature); return; }
    if (!agree) { toast.error(t.toastAgreement); return; }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contracts`, {
        ...form, addons, monthly_support: monthly,
        signature: sigRef.current.getDataURL(), agreement_accepted: agree, language: lang, invite: inviteToken,
      });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t.toastSubmitFail);
    } finally { setSubmitting(false); }
  };

  const LangToggle = () => (
    <div className="inline-flex items-center gap-1 border border-[#2a2a2a] bg-[#0d0d0d]/80 rounded-full p-1" data-testid="language-toggle">
      <Globe className="w-3.5 h-3.5 text-gold ml-2 mr-1" />
      {["en", "es"].map((l) => (
        <button
          key={l} type="button" onClick={() => setLang(l)}
          data-testid={`lang-${l}-btn`}
          className={`px-3 py-1 rounded-full text-xs font-sans uppercase tracking-widest transition-colors ${
            lang === l ? "bg-gold text-[#050505]" : "text-[#A3A3A3] hover:text-white"
          }`}
        >{l}</button>
      ))}
    </div>
  );

  if (inviteValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] zs-grain px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="max-w-md w-full text-center bg-[#0d0d0d] border border-[#262626] rounded-2xl p-10"
          data-testid="restricted-screen"
        >
          <img src={LOGO} alt="Zaynar Systems" className="w-16 h-16 mx-auto object-contain mb-5" />
          <Lock className="w-10 h-10 text-gold mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-light text-white">Private Access</h1>
          <p className="mt-4 font-sans text-sm text-[#A3A3A3] leading-relaxed">
            This Zaynar Systems portal can only be opened using a private invitation
            link provided by our team. Please contact Zaynar Systems to receive your secure link.
          </p>
          <p className="mt-6 font-sans text-xs uppercase tracking-widest text-[#555]">founder@zaynarsystems.info</p>
        </motion.div>
      </div>
    );
  }

  if (!entered) {
    return <LanguageGate onSelect={(l) => { setLang(l.code); setEntered(true); }} />;
  }

  if (!config) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] zs-grain px-6 text-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
        <p className="mt-5 font-serif text-xl text-white">Preparing your contract</p>
        <p className="mt-2 font-sans text-sm text-[#777]">Translating into your selected language…</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] zs-grain px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-xl w-full text-center bg-[#0d0d0d] border border-[#262626] rounded-2xl p-10 md:p-14 shadow-[0_0_60px_rgba(212,175,55,0.06)]"
          data-testid="success-screen"
        >
          <img src={LOGO} alt="Zaynar Systems" className="w-20 h-20 mx-auto object-contain mb-6" />
          <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-5" />
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-white mb-4">{t.successTitle}</h1>
          <p className="font-sans text-[#A3A3A3] leading-relaxed mb-8">{result.message}</p>
          <a
            href={`${API}/contracts/${result.contract_id}/pdf`} target="_blank" rel="noreferrer"
            data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 bg-gold text-[#050505] font-sans font-medium px-7 py-3.5 rounded-md hover:bg-gold-light transition-colors"
          >
            <Download className="w-4 h-4" /> {t.downloadPdf}
          </a>
          <p className="mt-6 text-xs font-sans uppercase tracking-widest text-[#555]">
            {t.referenceLabel}: {result.contract_id.slice(0, 8)} &nbsp;&bull;&nbsp; {result.contract_date}
          </p>
        </motion.div>
      </div>
    );
  }

  const inputClass = (key) =>
    `w-full bg-transparent border-b ${errors[key] ? "border-red-500" : "border-[#333]"} text-white px-0 py-3 rounded-none focus:outline-none focus:border-gold transition-colors font-sans placeholder-[#555]`;

  return (
    <div className="min-h-screen bg-[#050505] text-white zs-grain">
      {/* COVER */}
      <header className="relative overflow-hidden border-b border-[#1a1a1a]">
        <div className="absolute inset-0 opacity-[0.14] bg-cover bg-center" style={{ backgroundImage: `url('${HERO_TEXTURE}')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative max-w-4xl mx-auto px-6 md:px-12 pt-24 pb-16 md:pt-28 md:pb-24 text-center"
        >
          <img src={LOGO} alt="Zaynar Systems" className="w-24 h-24 md:w-28 md:h-28 mx-auto object-contain mb-8" data-testid="header-logo" />
          <Overline>{config.tagline}</Overline>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl md:text-7xl font-light tracking-tight text-white leading-[1.08]">
            Zaynar Systems
            <span className="block zs-gold-text">{t.titleLine2}</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto font-sans text-[#A3A3A3] leading-relaxed">{t.heroDescription}</p>
          <div className="mt-8 inline-flex items-center gap-3 border border-[#2a2a2a] bg-[#0d0d0d]/70 rounded-full px-5 py-2.5" data-testid="contract-date">
            <CalendarDays className="w-4 h-4 text-gold" />
            <span className="font-sans text-sm text-white tracking-wide">{t.contractDateLabel}: {config.contract_date}</span>
          </div>
        </motion.div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 flex flex-col gap-16 md:gap-24">
        {/* SERVICES */}
        <motion.section {...reveal}>
          <Overline>{t.servicesOverline}</Overline>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight">{t.servicesTitle}</h2>
          <p className="mt-3 font-sans text-[#A3A3A3] max-w-2xl">{t.servicesIntro}</p>

          <div data-testid="mandatory-service-card" className="mt-8 relative overflow-hidden bg-[#121212] border border-[#3a3320] rounded-xl p-6 md:p-8 ring-1 ring-gold/20">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="mt-0.5 w-10 h-10 rounded-lg bg-gold/15 flex items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-serif text-xl sm:text-2xl font-medium text-white">{config.mandatory_service.name}</h3>
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-sans uppercase tracking-widest text-gold border border-gold/40 rounded-full px-2.5 py-1">
                      <Lock className="w-3 h-3" /> {t.mandatoryBadge}
                    </span>
                  </div>
                  <p className="mt-2 font-sans text-sm text-[#A3A3A3] leading-relaxed max-w-xl">{config.mandatory_service.description}</p>
                </div>
              </div>
              <Lock className="w-5 h-5 text-[#666] shrink-0" />
            </div>
          </div>

          <p className="mt-10 mb-4"><Overline>{t.optionalOverline}</Overline></p>
          <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
            {config.addon_services.map((s) => {
              const active = addons.includes(s.id);
              return (
                <motion.button
                  key={s.id} type="button" whileHover={{ scale: 1.015 }}
                  onClick={() => toggleAddon(s.id)} data-testid={`addon-service-${s.id}`}
                  className={`text-left rounded-xl p-5 md:p-6 transition-all duration-300 ${
                    active ? "bg-[#1A1A14] border border-gold shadow-[0_0_20px_rgba(212,175,55,0.08)]"
                           : "bg-[#121212] border border-[#262626] hover:border-[#3a3a3a]"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-sans font-medium text-white">{s.name}</h3>
                    <span className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${active ? "bg-gold border-gold" : "border-[#444]"}`}>
                      {active && <Check className="w-3.5 h-3.5 text-[#050505]" />}
                    </span>
                  </div>
                  <p className="mt-2 font-sans text-sm text-[#A3A3A3] leading-relaxed">{s.description}</p>
                </motion.button>
              );
            })}
          </div>
        </motion.section>

        {/* MONTHLY SUBSCRIPTION */}
        <motion.section {...reveal}>
          <Overline>{t.monthlyOverline}</Overline>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight">{t.monthlyTitle}</h2>
          <button
            type="button" onClick={() => setMonthly((m) => !m)} data-testid="monthly-support-card"
            className={`mt-6 w-full text-left rounded-xl p-6 md:p-8 transition-all duration-300 ${
              monthly ? "bg-[#1A1A14] border border-gold shadow-[0_0_24px_rgba(212,175,55,0.1)]"
                      : "bg-[#121212] border border-[#262626] hover:border-[#3a3a3a]"}`}
          >
            <div className="flex items-start gap-4">
              <span className={`mt-1 shrink-0 w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${monthly ? "bg-gold border-gold" : "border-[#555]"}`}>
                {monthly && <Check className="w-4 h-4 text-[#050505]" />}
              </span>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-gold" /> {config.monthly_subscription.name}
                </h3>
                <p className="mt-3 font-sans text-sm text-[#A3A3A3] leading-relaxed">{config.monthly_subscription.description}</p>
                {monthly && <p className="mt-4 text-xs font-sans uppercase tracking-widest text-gold">{t.monthlyAccepted}</p>}
              </div>
            </div>
          </button>
        </motion.section>

        {/* CLIENT INFORMATION */}
        <motion.section {...reveal}>
          <Overline>{t.partiesOverline}</Overline>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight">{t.clientTitle}</h2>
          <p className="mt-3 font-sans text-[#A3A3A3]">{t.clientIntro}</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-8">
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 flex items-center gap-2"><User className="w-4 h-4 text-gold" /> {t.fullNameLabel}</label>
              <input data-testid="client-name-input" className={inputClass("full_name")} value={form.full_name}
                onChange={(e) => onField("full_name", e.target.value)} placeholder={t.fullNamePlaceholder} />
              {errors.full_name && <p className="mt-1 text-xs text-red-400 font-sans">{errors.full_name}</p>}
            </div>
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 flex items-center gap-2"><Building2 className="w-4 h-4 text-gold" /> {t.companyLabel}</label>
              <input data-testid="client-company-input" className={inputClass("company_name")} value={form.company_name}
                onChange={(e) => onField("company_name", e.target.value)} placeholder={t.companyPlaceholder} />
              {errors.company_name && <p className="mt-1 text-xs text-red-400 font-sans">{errors.company_name}</p>}
            </div>
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 flex items-center gap-2"><Mail className="w-4 h-4 text-gold" /> {t.emailLabel}</label>
              <div className="relative">
                <input data-testid="client-email-input" type="email" className={inputClass("email")} value={form.email}
                  onChange={(e) => onField("email", e.target.value)} onBlur={validateEmail} placeholder={t.emailPlaceholder} />
                {checking.email && <Loader2 className="w-4 h-4 text-gold animate-spin absolute right-0 top-3.5" />}
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-400 font-sans" data-testid="email-error">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 flex items-center gap-2"><Phone className="w-4 h-4 text-gold" /> {t.phoneLabel}</label>
              <div className="relative">
                <input data-testid="client-phone-input" inputMode="tel" className={inputClass("phone")} value={form.phone}
                  onChange={(e) => onPhoneInput(e.target.value)} onBlur={validatePhone} placeholder={t.phonePlaceholder} />
                {checking.phone && <Loader2 className="w-4 h-4 text-gold animate-spin absolute right-0 top-3.5" />}
              </div>
              {errors.phone && <p className="mt-1 text-xs text-red-400 font-sans" data-testid="phone-error">{errors.phone}</p>}
            </div>
          </div>
        </motion.section>

        {/* TERMS */}
        <motion.section {...reveal}>
          <Overline>{t.termsOverline}</Overline>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight">{t.termsTitle}</h2>
          <div className="mt-6 bg-[#0d0d0d] border border-[#1f1f1f] rounded-xl px-5 md:px-8">
            <Accordion type="single" collapsible className="w-full">
              {config.legal_sections.map((sec, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[#1f1f1f]">
                  <AccordionTrigger className="font-serif text-lg font-medium hover:text-gold text-left py-5">
                    <span><span className="text-gold mr-3">{String(i + 1).padStart(2, "0")}</span>{sec.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm text-[#A3A3A3] font-sans leading-relaxed space-y-3">
                    {sec.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </motion.section>

        {/* SIGNATURE */}
        <motion.section {...reveal}>
          <Overline>{t.signatureOverline}</Overline>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-light tracking-tight flex items-center gap-3">
            <PenLine className="w-7 h-7 text-gold" /> {t.signatureTitle}
          </h2>
          <p className="mt-4 font-sans text-sm text-[#A3A3A3] leading-relaxed max-w-2xl">{config.signature_declaration}</p>
          <div className="mt-6 max-w-xl">
            <SignaturePad ref={sigRef} placeholder={t.signaturePlaceholder} clearLabel={t.clearSignature} />
          </div>

          <div className="mt-10 flex items-start gap-4 bg-[#0d0d0d] border border-[#1f1f1f] rounded-xl p-5 md:p-6">
            <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)}
              data-testid="agreement-checkbox" className="mt-1 border-[#555] data-[state=checked]:bg-gold data-[state=checked]:border-gold data-[state=checked]:text-[#050505]" />
            <label htmlFor="agree" className="font-sans text-sm text-[#cfcfcf] leading-relaxed cursor-pointer">{config.agreement_confirmation}</label>
          </div>

          <button
            type="button" onClick={handleSubmit} disabled={submitting} data-testid="submit-contract-btn"
            className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold text-[#050505] font-sans font-medium px-10 py-4 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> {t.submitting}</> : <><ShieldCheck className="w-5 h-5" /> {t.submit}</>}
          </button>
          <p className="mt-4 text-xs font-sans text-[#555] flex items-center gap-2">
            <Lock className="w-3 h-3" /> {t.submitNote}
          </p>
        </motion.section>
      </main>

      <footer className="border-t border-[#1a1a1a] py-10 text-center">
        <img src={LOGO} alt="Zaynar Systems" className="w-12 h-12 mx-auto object-contain mb-3 opacity-80" />
        <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#555]">
          Zaynar Systems &nbsp;&bull;&nbsp; {t.footer} &nbsp;&bull;&nbsp; founder@zaynarsystems.info
        </p>
      </footer>
    </div>
  );
}
